/* eslint-disable fp/no-mutation */
import type { GroupV1_jsonld_page_read } from '@/features/group-v1/group-v1-type';

import React from 'react';

import debounce from 'lodash.debounce';
import dynamic from 'next/dynamic';

import { MagnifyingGlassIcon } from '@/assets/icons/magnifying-glass';
import { IconImage, LoopedLoadingBar } from '@/core/features';
import useDynamicPageComponentByType from '@/core/features/a-dynamic-page/hooks/use-dynamic-page-component-by-type';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import useLongPress from '@/core/features/event-listener/use-long-press';
import logger from '@/core/features/logger/logger';
import { getHeaderContainer, scrollPageContainerToTop } from '@/core/features/page/page-element-serivce';
import { scrollToTop } from '@/core/features/scroll/scroll-service';
import color from '@/core/features/styles/color';
import zIndex from '@/core/features/styles/generated/z-index';
import { WIREFRAME_HEADER_HEIGHT, WIREFRAME_SEARCH_OPEN_CLASS_MOBILE } from '@/core/features/wireframe/constants';
import {
    classC24mContactWrapper,
    classC24mSearchIconWrapper,
    idC24mPageBlockingLayer,
} from '@/core/features/wireframe/generated/wireframe-html-selectors-mobile';
import useAutoHideWireframe from '@/core/features/wireframe/use-auto-hide-wireframe';
import { useSelectedFilterCount } from '@/features/filter/filter-data/filter-data-attributes-state';
import { useFilterOverlayToggle } from '@/features/filter/filter-data/filter-state';
import useTravelFormCollapseLoading from '@/features/travel-form/travel-form-collapsed/use-travel-form-collapsed-loading';
import useTravelFormCta from '@/features/travel-form/travel-form-state';

import styles from './travel-form-collapsed-exploit-header.module.scss';

const FilterOverlayContainer = dynamic(
    () => import(/* webpackChunkName: "FilterOverlay" */ '@/features/filter/filter-overlay/filter-overlay-container'),
);

type TravelFormCollapsedProps = {
    componentId: string;
    ctaText: string;
    fullWidth: boolean;
    hasFilter: boolean;
    isPageLoading?: boolean;
    isSticky: boolean;
    loadingStateHandling?: 'auto' | 'manual';
    onClick?: () => void;
    subtitle: null | string;
    title: null | string;
};

export default function TravelFormCollapsedExploitHeader({
    componentId,
    ctaText,
    fullWidth,
    hasFilter,
    isPageLoading,
    isSticky,
    loadingStateHandling = 'manual',
    onClick,
    subtitle,
    title,
}: TravelFormCollapsedProps) {
    const exploitHeaderElementRef = React.useRef<HTMLDivElement | null>(null);
    const travelFormCollapsedContentRef = React.useRef<HTMLDivElement>(null);

    const group = useDynamicPageComponentByType<GroupV1_jsonld_page_read>(
        'GroupV1',
        (component) => component.meta.style === 'map_bottom_sheet',
    );
    const isBottomSheetInitialOpen = React.useRef(['full', 'half'].includes(group?.meta.bottomSheetState ?? ''));

    const isDefaultForm = !subtitle && !title;

    const [isExploitHeaderHidden, setExploitHeaderHidden] = React.useState(false);

    const { setHeaderSticky } = useAutoHideWireframe(isBottomSheetInitialOpen.current);

    const [exploitHeaderHeight, setExploitHeaderHeight] = React.useState(0);

    const { isApp } = useDeviceoutput();
    const currentWireframeHeaderHeight = isApp ? exploitHeaderHeight : WIREFRAME_HEADER_HEIGHT;

    useLongPress({
        element: exploitHeaderElementRef.current,
        onLongPress: () => {
            setHeaderSticky();
            scrollPageContainerToTop();
            scrollToTop();
        },
    });

    /**
     * Effect: set header sticky on touchmove on header elements
     */
    const exploitHeaderElement = exploitHeaderElementRef.current;
    React.useEffect(() => {
        const header = getHeaderContainer();

        if (!header || !exploitHeaderElement) {
            return;
        }

        // prettier-ignore
        const touchmoveHandler = debounce(setHeaderSticky, 50, { leading: true });

        header.addEventListener('touchmove', touchmoveHandler);
        exploitHeaderElement.addEventListener('touchmove', touchmoveHandler);

        return () => {
            header.removeEventListener('touchmove', touchmoveHandler);
            exploitHeaderElement.removeEventListener('touchmove', touchmoveHandler);
        };
    }, [exploitHeaderElement, setHeaderSticky]);

    const { isTravelFormLoading, onCollapsedFormClick } = useTravelFormCta();
    const { isOverlayOpen, openOverlay } = useFilterOverlayToggle({
        overlayKey: 'filter-data-overlay',
    });
    const selectedFilterCount = useSelectedFilterCount();

    const onClickWrapper = () => {
        onCollapsedFormClick();
        onClick?.();
    };

    const showPageLoading = useTravelFormCollapseLoading({
        isPageLoading,
        loadingStateHandling,
    });

    const setHeaderZIndex = React.useCallback((retryCount = 0) => {
        if (retryCount > 10) {
            logger.error('Could not set header z-index in TravelFormCollapsedWrapper after 10 retries');
            return;
        }

        const header = getHeaderContainer();

        if (header?.classList.contains('placeholder')) {
            setTimeout(() => {
                setHeaderZIndex(retryCount++);
            }, 500);
        }

        if (header) {
            // eslint-disable-next-line fp/no-mutation
            header.style.zIndex = zIndex('travelFormCollapsedWrapper');

            return () => {
                // eslint-disable-next-line fp/no-mutation
                header.style.zIndex = '';
            };
        }

        return;
    }, []);

    React.useEffect(() => {
        setHeaderZIndex();
    }, [setHeaderZIndex]);

    // for webview we need the exploit header height to offset the loading bar vertically
    const measureExploitHeaderElementRef = React.useCallback((node: HTMLDivElement | null) => {
        if (node) {
            exploitHeaderElementRef.current = node;
            setExploitHeaderHeight(node.getBoundingClientRect().height);
        }
    }, []);

    /**
     * Effect: create backdrop element to cover the header when it is fixed, because of z-index-juggling it has to be high in the DOM
     */
    React.useEffect(() => {
        if (isApp) {
            return;
        }
        const headerContainer = getHeaderContainer();
        if (!headerContainer) {
            return;
        }

        const backdrop = document.createElement('div');
        backdrop.id = 'exploit-header-backdrop';
        backdrop.classList.add('absolute-top', 'bg-catalinaBlue');
        backdrop.style.height = `${WIREFRAME_HEADER_HEIGHT + 1}px`;
        backdrop.style.top = `${currentWireframeHeaderHeight - 1}px`;
        headerContainer.after(backdrop);

        return () => {
            backdrop.remove();
        };
    }, [currentWireframeHeaderHeight, isApp]);

    const hideTravelFormCollapsedContent = () => {
        travelFormCollapsedContentRef.current?.classList.add('hidden');
    };

    const showTravelFormCollapsedContent = () => {
        travelFormCollapsedContentRef.current?.classList.remove('hidden');
    };

    const toggleExploitHeader = () => {
        setExploitHeaderHidden(!!document.querySelector(`.${WIREFRAME_SEARCH_OPEN_CLASS_MOBILE}`));
    };

    /**
     * Effect: hide exploit header on contact layer or search click
     */
    React.useEffect(() => {
        const contactWrapper = document.querySelector(`.${classC24mContactWrapper}`);
        const blockingLayer = document.querySelector(`#${idC24mPageBlockingLayer}`);
        const searchIconWrapper = document.querySelector(`.${classC24mSearchIconWrapper}`);

        const onContactClick = () => {
            if (contactWrapper?.querySelector('.open')) {
                hideTravelFormCollapsedContent();
                return;
            }
            showTravelFormCollapsedContent();
        };

        contactWrapper?.addEventListener('click', onContactClick);
        blockingLayer?.addEventListener('click', showTravelFormCollapsedContent);
        searchIconWrapper?.addEventListener('click', toggleExploitHeader);

        return () => {
            searchIconWrapper?.removeEventListener('click', toggleExploitHeader);
            contactWrapper?.removeEventListener('click', onContactClick);
            blockingLayer?.removeEventListener('click', showTravelFormCollapsedContent);
        };
    }, []);

    return (
        <>
            <style jsx>{`
                /** fix SSR rendering white backgrounds */
                :global(#exploit-header) {
                    box-shadow:
                        -100px 0px 0px 0px var(--color-catalinaBlue),
                        100px 0px 0px 0px var(--color-catalinaBlue),
                        inset 0 -80px 0px 0px var(--color-catalinaBlue);
                }
                :global(body:has(#exploit-header-backdrop) #exploit-header) {
                    box-shadow: none;
                }
            `}</style>

            <div
                className={`flex-center ${styles.travelFormCollapsedWrapper}`}
                data-qa-id={'qa-travel-form-collapsed'}
                data-sticky={'TravelFormCollapsed'}
                id={'exploit-header'}
                ref={measureExploitHeaderElementRef}
                style={{
                    display: isExploitHeaderHidden ? 'none' : undefined,
                    marginBottom: fullWidth ? -33 : undefined,
                    paddingRight: !hasFilter && isApp ? 47 : undefined, // app needs padding for blue background
                    position: isSticky ? 'sticky' : undefined,
                    top: isSticky ? 0 : undefined,

                    /**
                     * width calculation:
                     * viewport width - (10px left page padding + 34 margin left for header pos)
                     * if no filter is present an addtional - (37px filter element + 10px margin left)
                     *
                     * for app webview:
                     * not quite sure how this values is composed but it fixes different sized exploit headers on different pages
                     * (e.g. s2 and course-detail fixture pages), there is probably an issue
                     * with different page header implementations which influence the width calculation of the exploit header
                     */

                    width: !isApp ? (hasFilter ? 'calc(100vw - 44px)' : 'calc(100vw - 91px)') : 'calc(100vw + 32px)',
                    zIndex: zIndex('travelFormCollapsedWrapper'),
                }}
            >
                <div
                    className={`width-100 flex-space-between-horizontal ${styles.travelFormCollapsedContent}`}
                    ref={travelFormCollapsedContentRef}
                >
                    <div
                        className={'width-100 padding-2 text-center grow-1 bg-mySin border-radius-3'}
                        onClick={onClickWrapper}
                    >
                        <div
                            className={`flex-space-between-horizontal bg-white font-size-14 border-radius-2 padding-2 ${styles.travelFormText}`}
                        >
                            {isDefaultForm ? (
                                <div className={'width-100 padding-5 flex'}>
                                    <MagnifyingGlassIcon
                                        className={'absolute'}
                                        color={color('mineShaft')}
                                        height={14}
                                        width={14}
                                    />
                                    <span
                                        className={'width-100 font-size-12 bold'}
                                        style={{ marginBottom: -2 }}
                                    >
                                        {ctaText}
                                    </span>
                                </div>
                            ) : (
                                <span className={'width-100'}>
                                    <span className={'line-clamp-1 font-size-12 bold'}>{title}</span>
                                    <span className={'line-clamp-1 font-size-10'}>{subtitle}</span>
                                </span>
                            )}
                        </div>
                    </div>
                    {hasFilter && (
                        <div
                            className={`bg-catalinaBlue padding-6 margin-left-10 ${styles.filterButton}`}
                            onClick={openOverlay}
                        >
                            <>
                                <IconImage
                                    height={25}
                                    url={
                                        'https://cdn.individualreisen.check24.de/static-images/icon-filter-round-v2.svg'
                                    }
                                    width={25}
                                />
                                {selectedFilterCount > 0 && (
                                    <div className={styles.activeFilter}>
                                        <span className={styles.activeFilterIconBorder}></span>
                                        <span className={`flex-center ${styles.activeFilterIconText}`}>
                                            {selectedFilterCount}
                                        </span>
                                    </div>
                                )}
                            </>
                        </div>
                    )}
                </div>
                {(isTravelFormLoading || showPageLoading) && (
                    <LoopedLoadingBar
                        // eslint-disable-next-line no-inline-styles/no-inline-styles
                        style={{ left: isApp ? 42 : -34, top: currentWireframeHeaderHeight, width: '100vw' }}
                    />
                )}
            </div>
            {isOverlayOpen && <FilterOverlayContainer componentId={componentId} />}
        </>
    );
}
